import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  Loader2,
  MessageCircleQuestion,
  Plus,
  Tag,
  Tags,
  Trash2,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useSaveCallGuide } from '../../../services/onboardingBoard';

// Call guide editor (Nic 2026-09-23, managers): the talk track the Sales Rep
// reads in the call workspace once contact is made, plus the "If they
// ask..." answers. {first name} in any text becomes the lead's first name.

const inputCls =
  'w-full rounded-lg bg-white px-3 py-2 text-sm text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10 dark:focus:ring-white/25';
const iconBtn =
  'rounded-lg p-1.5 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 focus:outline-none disabled:opacity-30 dark:hover:!bg-white/10';

// A section's title: icon, bold name, one line of what it is for, and a
// rule to the edge, so the two halves of the guide are easy to tell apart.
const SectionTitle = ({ icon: Icon, title, sub }) => (
  <div className="mb-3 flex items-center gap-2.5">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:!bg-indigo-500/15 dark:text-indigo-300">
      <Icon size={16} />
    </span>
    <div className="min-w-0">
      <div className="text-base font-bold text-slate-800 dark:text-slate-100">{title}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{sub}</div>
    </div>
    <span className="ml-2 h-px flex-1 bg-slate-200 dark:!bg-white/10" />
  </div>
);

// Filter tags (Nic 2026-10-26): short group labels, like "Cost", that the
// Sales Workspace filters answers by. Enter or a comma adds one, x removes
// it, Backspace on an empty field removes the last. Tags already used on
// other answers are offered first, so "Cost" stays one spelling.
export const FAQ_TAG_MAX = 5;
export const TagInput = ({ tags = [], known = [], onChange }) => {
  const [draft, setDraft] = useState('');
  const inputRef = React.useRef(null);
  const add = (raw) => {
    const t = String(raw || '').trim().replace(/\s+/g, ' ').slice(0, 30);
    if (!t || tags.length >= FAQ_TAG_MAX) return;
    // The spelling already in use wins ("cost" becomes "Cost").
    const same = known.find((k) => k.toLowerCase() === t.toLowerCase());
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    onChange([...tags, same || t]);
  };
  const offers = draft.trim()
    ? known.filter((k) => k.toLowerCase().startsWith(draft.trim().toLowerCase()) && !tags.some((x) => x.toLowerCase() === k.toLowerCase()))
    : [];
  return (
    <div className="relative">
      {/* A div, not a label (QA 2026-10-26): a label with no htmlFor clicks its
          first control, the first chip's Remove, so a click anywhere removed a
          tag. A click on the box (not a chip button) focuses the input. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={(e) => {
          if (!e.target.closest('button')) inputRef.current?.focus();
        }}
        className="flex min-h-[34px] cursor-text flex-wrap items-center gap-1.5 rounded-lg bg-white px-3 py-1 ring-1 ring-slate-200 focus-within:ring-slate-300 dark:!bg-white/5 dark:ring-white/10 dark:focus-within:ring-white/25">
        <Tags size={13} className="shrink-0 text-slate-400" />
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 py-0.5 pl-2 pr-1 text-[11px] font-semibold text-indigo-700 dark:!bg-indigo-500/15 dark:text-indigo-200"
          >
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              onClick={() => onChange(tags.filter((x) => x !== t))}
              className="rounded-full p-0.5 outline-none hover:bg-indigo-100 focus:outline-none dark:hover:!bg-indigo-500/25"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        {tags.length < FAQ_TAG_MAX ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                add(draft);
                setDraft('');
              } else if (e.key === 'Backspace' && !draft && tags.length) {
                onChange(tags.slice(0, -1));
              }
            }}
            onBlur={() => {
              if (draft.trim()) add(draft);
              setDraft('');
            }}
            placeholder={tags.length ? '' : 'Filter tags, like Cost'}
            aria-label="Filter tags"
            className="min-w-[90px] flex-1 border-0 bg-transparent py-1 text-xs text-slate-700 outline-none focus:outline-none dark:text-slate-100"
          />
        ) : null}
      </div>
      {offers.length ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 flex flex-wrap gap-1 rounded-lg bg-white p-1.5 shadow-lg ring-1 ring-slate-200 dark:!bg-surface-elevated dark:ring-white/10">
          {offers.map((k) => (
            <button
              key={k}
              type="button"
              // Before the input's blur adds the half-typed text.
              onMouseDown={(e) => {
                e.preventDefault();
                add(k);
                setDraft('');
              }}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 outline-none hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none dark:!bg-white/10 dark:text-slate-300"
            >
              {k}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const move = (list, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// Stable row keys: index keys made a delete or a move hand one row's
// half-typed text to its neighbour. Stripped before save.
let nextKey = 0;
const keyed = (row) => ({ ...row, _k: (nextKey += 1) });
const unkeyed = (rows) => rows.map(({ _k, ...row }) => row);
const snapshot = (sections, faqs) =>
  JSON.stringify({ sections: unkeyed(sections), faqs: unkeyed(faqs) });

const CallGuideEditor = ({ open, onClose, guide }) => {
  const queryClient = useQueryClient();
  const [sections, setSections] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const save = useSaveCallGuide();

  useEffect(() => {
    if (!open) return;
    const nextSections = (guide?.sections || []).map(keyed);
    const nextFaqs = (guide?.faqs || []).map(keyed);
    setSections(nextSections);
    setFaqs(nextFaqs);
    setSaved(snapshot(nextSections, nextFaqs));
    setErr('');
  }, [open, guide]);

  // Every filter tag in use, for the suggestions (one spelling each).
  const knownTags = [
    ...faqs
      .flatMap((f) => f.tags || [])
      .reduce((m, t) => (m.has(t.toLowerCase()) ? m : m.set(t.toLowerCase(), t)), new Map())
      .values(),
  ].sort((x, y) => x.localeCompare(y));
  const dirty = open && snapshot(sections, faqs) !== saved;
  // Closing with edits asks first (Escape, the backdrop, X and Cancel).
  const requestClose = () => {
    if (dirty && !window.confirm('Discard your changes to the call guide?')) return;
    onClose();
  };
  const closeRef = React.useRef(requestClose);
  closeRef.current = requestClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const doSave = async () => {
    try {
      await save.mutateAsync({ sections: unkeyed(sections), faqs: unkeyed(faqs) });
      queryClient.invalidateQueries(['onboarding-board']);
      toast.success('Call guide saved.');
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.msg || "The call guide couldn't be saved.");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1070]">
      <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60" onClick={requestClose} />
      <div className="absolute inset-2 mx-auto flex max-w-4xl flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] shadow-2xl md:inset-y-6 dark:bg-surface-page">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3 dark:border-white/10 dark:!bg-surface-elevated">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900">
            <BookOpen size={16} />
          </span>
          <div className="min-w-0">
            <div className="text-base font-bold text-slate-800 dark:text-slate-100">Call guide</div>
            <div className="text-xs text-slate-400">
              What reps read in the call workspace once contact is made. Use {'{first name}'} for the lead&apos;s name.
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="ml-auto rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:!bg-white/10"
          >
            <X size={18} />
          </button>
        </header>

        <div className="os-scroll min-h-0 flex-1 space-y-6 overflow-y-auto p-5">
          <section>
            {/* Section titles that stand out (Nic 2026-10-05: the small
                caps labels were too easy to miss). */}
            <SectionTitle
              icon={BookOpen}
              title="Talk track"
              sub="The steps of the call, in order."
            />
            <ol className="mb-0 list-none space-y-2 pl-0">
              {sections.map((sec, i) => (
                <li
                  key={sec._k}
                  className="rounded-2xl bg-white p-3 shadow-sm dark:!bg-surface-card dark:ring-1 dark:ring-white/10"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white dark:!bg-slate-100 dark:!text-slate-900">
                      {i + 1}
                    </span>
                    <input
                      value={sec.title}
                      onChange={(e) =>
                        setSections(sections.map((s, k) => (k === i ? { ...s, title: e.target.value } : s)))
                      }
                      placeholder="Step title"
                      className={cn(inputCls, 'font-semibold')}
                    />
                    <button type="button" className={iconBtn} disabled={i === 0} onClick={() => setSections(move(sections, i, -1))}>
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      className={iconBtn}
                      disabled={i === sections.length - 1}
                      onClick={() => setSections(move(sections, i, 1))}
                    >
                      <ArrowDown size={14} />
                    </button>
                    <button
                      type="button"
                      className={iconBtn}
                      onClick={() => setSections(sections.filter((_, k) => k !== i))}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <textarea
                    value={sec.body}
                    rows={3}
                    onChange={(e) =>
                      setSections(sections.map((s, k) => (k === i ? { ...s, body: e.target.value } : s)))
                    }
                    placeholder="What to say"
                    className={cn(inputCls, 'resize-y leading-relaxed')}
                  />
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => setSections([...sections, keyed({ title: '', body: '' })])}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:!bg-white/5 whitespace-nowrap"
            >
              <Plus size={14} /> Add a step
            </button>
          </section>

          <section>
            <SectionTitle
              icon={MessageCircleQuestion}
              title="If they ask"
              sub="Answers to common questions. Keywords help reps find them; filter tags group them."
            />
            <div className="space-y-2">
              {faqs.map((f, i) => (
                <div
                  key={f._k}
                  className="rounded-2xl bg-white p-3 shadow-sm dark:!bg-surface-card dark:ring-1 dark:ring-white/10"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <input
                      value={f.q}
                      onChange={(e) => setFaqs(faqs.map((x, k) => (k === i ? { ...x, q: e.target.value } : x)))}
                      placeholder="The question"
                      className={cn(inputCls, 'font-semibold')}
                    />
                    <button type="button" className={iconBtn} onClick={() => setFaqs(faqs.filter((_, k) => k !== i))}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <textarea
                    value={f.a}
                    rows={2}
                    onChange={(e) => setFaqs(faqs.map((x, k) => (k === i ? { ...x, a: e.target.value } : x)))}
                    placeholder="How to answer"
                    className={cn(inputCls, 'resize-y leading-relaxed')}
                  />
                  {/* Keywords (Nic 2026-09-30): extra words that find this
                      answer in the Call Desk search. */}
                  {/* Icon and field side by side in one bordered row (an
                      absolutely placed icon sat on the placeholder). */}
                  {/* Half and half (Nic 2026-10-26): keywords for the
                      search, filter tags for the chips under it. */}
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <label className="flex min-h-[34px] items-center gap-2 rounded-lg bg-white px-3 ring-1 ring-slate-200 focus-within:ring-slate-300 dark:!bg-white/5 dark:ring-white/10 dark:focus-within:ring-white/25">
                      <Tag size={13} className="shrink-0 text-slate-400" />
                      <input
                        value={f.keywords || ''}
                        onChange={(e) =>
                          setFaqs(faqs.map((x, k) => (k === i ? { ...x, keywords: e.target.value } : x)))
                        }
                        placeholder="Keywords that find this answer"
                        className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-xs text-slate-700 outline-none focus:outline-none dark:text-slate-100"
                      />
                    </label>
                    <TagInput
                      tags={f.tags || []}
                      known={knownTags}
                      onChange={(tags) => setFaqs(faqs.map((x, k) => (k === i ? { ...x, tags } : x)))}
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setFaqs([...faqs, keyed({ q: '', a: '' })])}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:!bg-white/5 whitespace-nowrap"
            >
              <Plus size={14} /> Add a question
            </button>
          </section>
        </div>

        <footer className="flex items-center gap-3 border-t border-slate-200 bg-white px-5 py-3 dark:border-white/10 dark:!bg-surface-elevated">
          {err ? <span className="text-xs text-rose-600 dark:text-rose-400">{err}</span> : null}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={requestClose}
              className="rounded-lg px-3.5 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:!bg-white/10 whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={save.isLoading}
              onClick={doSave}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:!text-slate-900 whitespace-nowrap"
            >
              {save.isLoading ? <Loader2 size={14} className="animate-spin" /> : null}
              Save guide
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default CallGuideEditor;
